<script setup lang="ts">
const { carId, onClose } = defineProps<{
  carId: number;
  onClose: () => void;
}>();

const { t } = useI18n({
  useScope: "global"
});

const scroll = inject("modalBodyScroll") as ReturnType<typeof useScroll>;

const submitting = ref(false);

const carTermsModal = reactive({
  loading: true,
  error: null as unknown,
  result: null as { url?: string } | null
});

const onFormLoad = async () => {
  try {
    carTermsModal.loading = true;
    const client = useClient();
    const result = await client.car.termsPreview.mutate({
      carId
    });
    carTermsModal.result = result;
    carTermsModal.loading = false;
  } catch (e) {
    console.error(e);
    carTermsModal.loading = false;
    carTermsModal.error = e;
  }
};

onMounted(() => {
  onFormLoad();
});

const onSubmit = async (_formData: unknown, node: { setErrors: (e: string[]) => void }) => {
  try {
    node.setErrors([]);
    submitting.value = true;
    const client = useClient();
    await client.car.termsAccept.mutate({
      carId
    });
    submitting.value = false;
    onClose();
  } catch (e: unknown) {
    node.setErrors([e instanceof Error ? e.message : String(e)]);
    submitting.value = false;
    return false;
  }
};

const abortWithLogout = async () => {
  onClose();
  const authStore = useAuthUser();
  await authStore.logout();
};
</script>

<template>
  <template v-if="carTermsModal.loading">
    <UiLoader style="width: 100%; height: 400px" />
  </template>
  <template v-else-if="carTermsModal.error">
    <div>
      <div class="error">
        {{ carTermsModal.error }}
      </div>
      <FormKit
        type="submit"
        :label="t('modal.carTerms.logout')"
        @click="abortWithLogout"
      />
    </div>
  </template>
  <template v-else>
    <div class="car-terms">
      <div class="terms">
        <ClientOnly>
          <AppPdfPreview :link="carTermsModal.result?.url" />
        </ClientOnly>
      </div>
      <div class="footer">
        <FormKit
          id="car-terms"
          v-slot="{ value }"
          type="form"
          :actions="false"
          @submit="onSubmit"
        >
          <FormKit
            :disabled="!scroll?.arrivedState.bottom"
            name="terms"
            outer-class="checkbox"
            type="checkbox"
            :label="t('modal.carTerms.agreement')"
          />
          <FormKit
            type="group"
            variant="inline"
          >
            <FormKit
              :disabled="!value?.terms || submitting"
              type="submit"
              :label="t('modal.carTerms.submit')"
            />
          </FormKit>
        </FormKit>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.car-terms {
  position: relative;
  min-height: 40vh;
  max-width: 100%;

  .terms {
    max-width: 100%;
    overflow-x: auto;
  }

  .footer {
    position: sticky;
    bottom: 0;
    z-index: 1;
    width: 100%;
    background-color: #fff;
    padding: 10px 15px;
  }
}
</style>
